import styled, { css, keyframes } from "styled-components";
import {
  BORDER_COLORS,
  COLORS,
  ICON_COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_CSS } from "../../constants/design/fontSize";
import {
  BORDER_RADIUS,
  COMPONENTS,
  SPACING,
} from "../../constants/design/spacing";
import { TableWrapper } from "../shared/Table/style";
import { frostedGlassBg } from "../shared/style";

export const MarketsTableWrapper = styled(TableWrapper)`
  padding: 0 ${SPACING.two}px;
  overflow-x: scroll;

  tbody {
    tr {
      border-bottom: none !important;
    }
  }
`;

export const MarketsTableTable = styled.table`
  th {
    padding: 0 ${SPACING.two}px !important;
    background-color: transparent !important;
  }
  tbody {
    tr {
      cursor: pointer;
      border: none;
    }
  }
`;

export const MarketsTableCol = styled.td`
  padding: 0 ${SPACING.two}px !important;
  width: 20%;

  &:first-child {
    border-top-left-radius: ${BORDER_RADIUS.md}px;
    border-bottom-left-radius: ${BORDER_RADIUS.md}px;
    width: 140px;
  }

  &:last-child {
    border-top-right-radius: ${BORDER_RADIUS.md}px;
    border-bottom-right-radius: ${BORDER_RADIUS.md}px;
    width: 48px;
  }
`;

const rowAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateY(8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const MarketTableBody = styled.tbody``;

export const MarketRow = styled.tr<{
  isSelected?: boolean;
  delay: number;
  dragging?: boolean;
  dragOver?: boolean;
}>`
  color: ${({ isSelected }) =>
    isSelected ? TEXT_COLORS.one : TEXT_COLORS.two};
  ${({ isSelected }) =>
    isSelected &&
    css`
      background-color: ${LAYER_COLORS.two};
    `}
  opacity: 0;
  animation: ${rowAnimation} 0.2s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay}s;
  transition: opacity 0.2s ease, box-shadow 0.25s ease,
    transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1),
    background-color 0.15s ease;
  touch-action: pan-y;

  ${({ dragging }) =>
    dragging &&
    css`
      opacity: 0.95 !important;
      transform: scale(1.005);
      box-shadow: 0 10px 28px rgba(0, 0, 0, 0.55);
      background-color: ${LAYER_COLORS.three};
      position: relative;
      z-index: 10;
    `}

  ${({ dragOver }) =>
    dragOver &&
    css`
      transform: translateY(3px);
      box-shadow: inset 0 3px 0 0 ${COLORS.highlight.one};
      background-color: rgba(76, 215, 244, 0.06);
    `}
`;

export const AssetCellContainer = styled.div`
  color: ${TEXT_COLORS.one};
  display: flex;
  align-items: center;
  gap: ${SPACING.two}px;
`;

/**
 * `$value` is undefined when the 24h change is unknown. That must NOT paint
 * green: the old rule was `$value < 0 ? down : up`, so an unknown (and a
 * fabricated zero) rendered in the "up" colour, affirmatively signalling a flat
 * or rising market nobody had measured. Unknown is neutral and unfilled.
 */
export const ChangeTag = styled.div<{ $value?: number }>`
  border-radius: 4px;
  padding: ${SPACING.one}px;
  color: ${({ $value }) => {
    if ($value === undefined) return TEXT_COLORS.two;
    return $value < 0 ? COLORS.down.one : COLORS.up.one;
  }};
  background-color: ${({ $value }) => {
    if ($value === undefined) return "transparent";
    return $value < 0 ? COLORS.down.five : COLORS.up.five;
  }};
`;

export const WatchlistButton = styled.button`
  > svg {
    width: 16px;
    height: 16px;
  }
`;

export const CommandFooterContainer = styled.div`
  ${FONT_CSS.body.five}
  color: ${TEXT_COLORS.two};
  padding: ${SPACING.twoHalf}px ${SPACING.three}px;
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${COMPONENTS.cmdKModalFooter}px;
  border-top: 1px solid ${BORDER_COLORS.two};
  ${frostedGlassBg("rgba(18, 18, 26, 0.64)", LAYER_COLORS.one, 8)}
`;

export const FooterItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;

  &:first-child {
    justify-content: flex-start;
  }

  &:last-child {
    justify-content: flex-end;
  }

  div {
    background-color: ${LAYER_COLORS.three};
    padding: ${SPACING.one}px ${SPACING.two}px;
    border-radius: ${BORDER_RADIUS.two}px;

    &:nth-child(2) {
      margin-left: ${SPACING.one}px;
    }

    svg {
      width: 16px;
      height: 16px;
      stroke: ${ICON_COLORS.two};
    }
  }

  span {
    margin-left: ${SPACING.two}px;
  }
`;

export const PriceCell = styled.div<{
  isNegative?: boolean;
}>`
  > div:nth-child(2) {
    display: flex;
    justify-content: flex-end;
    margin-top: ${SPACING.one}px;
    margin-bottom: ${SPACING.two}px;
  }
`;
